<script setup lang="ts">
import { useDesignStore } from '../../store/useDesignStore'
import { RotateCw } from 'lucide-vue-next'
/** Auswahlrahmen mit 8 Griffen + Rotationsgriff, in Pixeln über der Fläche positioniert. */
import { computed } from 'vue'
import type { DesignCanvas, Layer } from '@pfe/design-renderer'
import type { HandleName } from '../../design/gestures/transformMath'

const props = defineProps<{
  layer: Layer
  canvas: DesignCanvas
  pxPerMm: number
  coarse?: boolean
  inSlot?: boolean
}>()
const emit = defineEmits<{
  (e: 'handle-down', handle: HandleName, ev: PointerEvent): void
  (e: 'rotate-down', ev: PointerEvent): void
}>()

const box = computed(() => {
  const f = props.layer.frame
  const s = props.pxPerMm
  return {
    left: `${(f.xMm + props.canvas.bleedMm) * s}px`,
    top: `${(f.yMm + props.canvas.bleedMm) * s}px`,
    width: `${f.wMm * s}px`,
    height: `${f.hMm * s}px`,
    transform: f.rotationDeg ? `rotate(${f.rotationDeg}deg)` : undefined,
  }
})
const handles: Array<{ name: HandleName; x: number; y: number; cursor: string }> = [
  { name: 'nw', x: 0, y: 0, cursor: 'nwse-resize' }, { name: 'n', x: 50, y: 0, cursor: 'ns-resize' }, { name: 'ne', x: 100, y: 0, cursor: 'nesw-resize' },
  { name: 'e', x: 100, y: 50, cursor: 'ew-resize' }, { name: 'se', x: 100, y: 100, cursor: 'nwse-resize' }, { name: 's', x: 50, y: 100, cursor: 'ns-resize' },
  { name: 'sw', x: 0, y: 100, cursor: 'nesw-resize' }, { name: 'w', x: 0, y: 50, cursor: 'ew-resize' },
]
// Im Vorlagen-Editor (Admin) gilt „nur Inhalt“ nicht – dort werden die Plätze ja gerade eingerichtet
const store = useDesignStore()
const canResize = computed(() => !props.layer.constraints.locked && (!props.layer.constraints.contentOnly || store.templateMode) && !props.inSlot)
// Bögen: Plätze werden nie gedreht (das Labor setzt gerade Plätze)
const canRotate = computed(() => canResize.value && store.activeDoc?.meta.kind !== 'sheet' && !store.activeDoc?.meta.sheet)
</script>

<template>
  <div class="lh" :class="{ 'lh--coarse': coarse, 'lh--locked': layer.constraints.locked, 'lh--slot': inSlot }" :style="box">
    <template v-if="canResize">
      <button
        v-for="h in handles"
        :key="h.name"
        type="button"
        class="lh__handle"
        :class="`lh__handle--${h.name}`"
        :style="{ left: `${h.x}%`, top: `${h.y}%`, cursor: h.cursor }"
        :aria-label="`Griff ${h.name}`"
        @pointerdown.stop.prevent="emit('handle-down', h.name, $event)"
      />
      <button v-if="canRotate" type="button" class="lh__rotate" aria-label="Drehen" @pointerdown.stop.prevent="emit('rotate-down', $event)"><RotateCw :size="12" aria-hidden="true" /></button>
    </template>
  </div>
</template>

<style scoped>
.lh { position: absolute; box-sizing: border-box; border: 1.5px solid var(--pfe-color-accent, #e30b17); pointer-events: none; transform-origin: center center; z-index: 5; }
.lh--locked { border-style: dashed; border-color: rgba(15, 23, 42, 0.5); }
.lh--slot { border-color: #2563eb; box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.25); }
.lh__handle { position: absolute; width: 12px; height: 12px; margin: -6px 0 0 -6px; border: 1.5px solid var(--pfe-color-accent, #e30b17); border-radius: 3px; background: #fff; padding: 0; pointer-events: auto; touch-action: none; }
.lh__handle::before { content: ''; position: absolute; inset: -10px; }
.lh--coarse .lh__handle { width: 16px; height: 16px; margin: -8px 0 0 -8px; }
.lh--coarse .lh__handle::before { inset: -12px; }
.lh__rotate { position: absolute; left: 50%; top: -34px; width: 26px; height: 26px; margin-left: -13px; border-radius: 50%; border: 1.5px solid var(--pfe-color-accent, #e30b17); background: #fff; font-size: 15px; line-height: 1; cursor: grab; pointer-events: auto; touch-action: none; padding: 0; display: inline-flex; align-items: center; justify-content: center; color: var(--pfe-color-accent, #e30b17); }
.lh--coarse .lh__rotate { top: -44px; width: 34px; height: 34px; margin-left: -17px; }
</style>
